import { useTranslation } from "../../../components/Translation";
import { localizeName } from "../../../utils/localizeName";
import DrCastillo from "/img/contactUs/DrCastillo.png";
import { useState } from "react";
import emailjs from "@emailjs/browser";

type InterestKey = "joint_research" | "industrial_consulting" | "postgraduate" | "other";

export default function ContactInfo() {
  const { t, language } = useTranslation();

  const [interest, setInterest] = useState<InterestKey>("joint_research");
  const [name, setName] = useState("");
  const [institution, setInstitution] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [subscribe, setSubscribe] = useState(true);
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  // Mismo formulario de MailerLite que el footer; solo se llama si la persona acepta
  const subscribeToNewsletter = () => {
    const data = new FormData();
    data.append("fields[email]", email);
    data.append("ml-submit", "1");
    data.append("anticsrf", "true");
    // no-cors: MailerLite no expone la respuesta; un fallo aquí no debe afectar el envío del mensaje
    return fetch("https://assets.mailerlite.com/jsonp/2746304/forms/202488790891103651/subscribe", {
      method: "POST",
      mode: "no-cors",
      body: data,
    }).catch((error: unknown) => console.error("MailerLite error:", error));
  };

  const handleSubmit = async () => {
    if (!name || !email || !message) {
      setStatus("error");
      return;
    }

    setStatus("sending");

    try {
      await emailjs.send(
        import.meta.env.VITE_EMAILJS_SERVICE_ID,
        import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
        {
          name,
          institution,
          email,
          interest: t(`contact.form_interest_${interest}`),
          message,
          time: new Date().toLocaleString("es-MX", {
            dateStyle: "long",
            timeStyle: "short",
          }),
        },
        import.meta.env.VITE_EMAILJS_PUBLIC_KEY
      );

      if (subscribe) await subscribeToNewsletter();

      setStatus("success");
      setName("");
      setInstitution("");
      setEmail("");
      setMessage("");
      setInterest("joint_research");
      setSubscribe(true);
    } catch (error) {
      console.error("EmailJS error:", error);
      setStatus("error");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-2 gap-10">
      {/* Left: Director card + location */}
      <div className="space-y-6">
        {/* Director card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex gap-4">
          <div className="flex items-center gap-2 mb-3 col-span-2">
            <svg className="w-5 h-5 text-dorado-uady" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <h3 className="font-bold text-azul-uady">
              {t("contact.director_title")}
            </h3>
          </div>

          <img
            src={DrCastillo}
            alt="Castillo"
            className="w-20 h-20 rounded-lg object-cover object-top flex-shrink-0"
          />

          <div>
            <p className="font-bold text-azul-uady text-mini">
              {localizeName("Dr. Alejandro Arturo Castillo Atoche", language)}
            </p>
            <p className="text-micro text-dorado-uady font-semibold uppercase tracking-wide mb-2">
              {t("contact.director_role")}
            </p>

            <div className="space-y-1">
              <p className="text-micro text-gray-500 flex items-center gap-1">
                <span>✉</span> acastill@correo.uady.mx
              </p>
              <p className="text-micro text-gray-500 flex items-center gap-1">
                <span>📞</span> +52 (999) 930 0550 Ext. 1234
              </p>
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="bg-[#0d1f35] rounded-xl p-5 text-white">
          <h3 className="font-bold mb-3 flex items-center gap-2">
            <span>📍</span> {t("contact.location_title")}
          </h3>

          <p className="text-white/70 text-mini leading-relaxed mb-3">
            {t("contact.location_line_1")}<br />
            {t("contact.location_line_2")}<br />
            {t("contact.location_line_3")}
          </p>

          <p className="text-dorado-uady text-mini font-mono font-semibold">
            {t("contact.location_building")}
          </p>
        </div>

        {/* Lab vivo card */}
        <div className="bg-dorado-uady rounded-xl p-6 text-white text-center">
          <div className="text-4xl mb-3">🤖</div>

          <h3 className="font-bold text-lg mb-2">
            {t("contact.living_lab_title")}
          </h3>

          <p className="text-white/80 text-mini mb-4">
            {t("contact.living_lab_description")}
          </p>

          <a
            href="mailto:acastill@correo.uady.mx"
            className="inline-block border-2 border-white text-white font-semibold text-mini px-6 py-2 rounded hover:bg-white hover:text-dorado-uady transition-colors"
          >
            {t("contact.living_lab_button")}
          </a>
        </div>
      </div>

      {/* Right: Collaboration form */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-7">
        <h2 className="text-2xl font-bold text-azul-uady mb-2">
          {t("contact.form_title")}
        </h2>

        <p className="text-mini text-gray-500 mb-6">
          {t("contact.form_description")}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-micro font-semibold text-gray-600 mb-1">
              {t("contact.form_name_label")}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("contact.form_name_placeholder")}
              className="w-full border-b border-gray-300 py-2 text-mini focus:outline-none focus:border-dorado-uady transition-colors"
            />
          </div>

          <div>
            <label className="block text-micro font-semibold text-gray-600 mb-1">
              {t("contact.form_institution_label")}
            </label>
            <input
              type="text"
              value={institution}
              onChange={(e) => setInstitution(e.target.value)}
              placeholder={t("contact.form_institution_placeholder")}
              className="w-full border-b border-gray-300 py-2 text-mini focus:outline-none focus:border-dorado-uady transition-colors"
            />
          </div>

          <div>
            <label className="block text-micro font-semibold text-gray-600 mb-1">
              {t("contact.form_email_label")}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("contact.form_email_placeholder")}
              className="w-full border-b border-gray-300 py-2 text-mini focus:outline-none focus:border-dorado-uady transition-colors"
            />
          </div>

          <div>
            <label className="block text-micro font-semibold text-gray-600 mb-1">
              {t("contact.form_interest_label")}
            </label>
            <select
              value={interest}
              onChange={(e) => setInterest(e.target.value as InterestKey)}
              className="w-full border-b border-gray-300 py-2 text-mini focus:outline-none focus:border-dorado-uady bg-transparent transition-colors"
            >
              <option value="joint_research">
                {t("contact.form_interest_joint_research")}
              </option>
              <option value="industrial_consulting">
                {t("contact.form_interest_industrial_consulting")}
              </option>
              <option value="postgraduate">
                {t("contact.form_interest_postgraduate")}
              </option>
              <option value="other">
                {t("contact.form_interest_other")}
              </option>
            </select>
          </div>

          <div className="col-span-1 sm:col-span-2">
            <label className="block text-micro font-semibold text-gray-600 mb-1">
              {t("contact.form_message_label")}
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("contact.form_message_placeholder")}
              rows={5}
              className="w-full border-b border-gray-300 py-2 text-mini focus:outline-none focus:border-dorado-uady resize-none transition-colors"
            />
          </div>

          <label className="col-span-1 sm:col-span-2 flex items-center gap-2 text-mini text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={subscribe}
              onChange={(e) => setSubscribe(e.target.checked)}
              className="w-4 h-4 accent-dorado-uady"
            />
            {t("contact.form_newsletter_label")}
          </label>
        </div>

        {/* Mensajes de estado */}
        {status === "success" && (
          <p className="text-mini text-green-600 font-semibold mt-4">
            ✓ {t("contact.form_success_message")}
          </p>
        )}

        {status === "error" && (
          <p className="text-mini text-red-500 font-semibold mt-4">
            ✕ {t("contact.form_error_message")}
          </p>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={handleSubmit}
            disabled={status === "sending"}
            className="bg-dorado-uady text-white font-semibold text-mini px-8 py-3 rounded hover:bg-yellow-600 transition-colors flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {status === "sending"
              ? t("contact.form_sending_button")
              : t("contact.form_submit_button")}
          </button>
        </div>
      </div>
    </div>
  );
}